/* eslint-disable  no-underscore-dangle */

import $ from 'jquery';
import pgrid from 'projection-grid';
import _ from 'underscore';
import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { DeterministicView } from '@bingads-webui/deterministic-view';
import { Dialog } from '@bingads-webui/dialog';
import { paginationPlugin } from '@bingads-webui/projection-grid-pagination';
import { toolbarPlugin } from '@bingads-webui/projection-grid-toolbar';
import { treePlugin } from '@bingads-webui/projection-grid-tree';
import { uiBlockerPlugin } from '@bingads-webui/projection-grid-uiblocker';
import { assistedSelectionPlugin } from '@bingads-webui/projection-grid-assisted-selection';
import { gridOptionUtils } from '../hotel-center-service';
import { HotelGroupModel, dateFilterPlugin } from '../hotel-center-shared';
import EditControl from '../assignment-utils/edit-control';
import GridOptions from './grid-options';
import template from './template.pug';

const i18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/hotel-center');
const { DownloadViewV1 } = globalContext.require('campaignui/feature/griddownload/view/adapter');
const edm = globalContext.requireDefault('campaignui/feature/hotel-center-edm/index');
const jsdata = globalContext.require('campaignui/feature/hotel-center-js-data/index');
const state = globalContext.require('component/state/index');

const ignoreServerPaginationPlugin = definePlugin => definePlugin('ignoreServerPaginationPlugin', [
  'config',
  'gridView',
], (config, gridView) => {
  const { dataSource } = gridView;
  const originalQuery = dataSource.query;

  dataSource.query = params => originalQuery.call(dataSource, _.extend(_.omit(params, 'skip', 'take'), { take: Number.MAX_VALUE }));
});

/**
 * Class representing hotel group list part for hotel center assignment
 * @extends View
 */
export default class HotelGroups extends DeterministicView {
  /**
  * Initializes HotelGroups
  * @returns {undefined}
  */
  initialize() {
    this.i18n = i18n;
    this.HotelGroupModel = HotelGroupModel;

    this.errorCodeToStringMap = {
      CreateFailed: i18n.getString('HotelCenter_Group_Create_Failed_Generic_Error_Message'),
      SameValueAlreadyExist: i18n.getString('HotelCenter_Group_Name_Exists_Error_Message'),
    };
    this.fetchAssociations()
      .then((response) => {
        this.disableDownload(response.totalCount === 0);
      });
    this.isSubAccountLevel = state.get('subAccount_id');
  }

  get events() {
    return {
      'click .create-button__container input': this.onShowCreateDialog,
      'click .associations-download__container input': this.onDownloadAssociations,
    };
  }

  get template() {
    return template;
  }

  renderTrunk() {
    super.renderTrunk();

    this.gridOptions = new GridOptions();

    let gridFactory = pgrid.factory({ vnext: true })
      .use(toolbarPlugin)
      .use(dateFilterPlugin)
      .use(uiBlockerPlugin)
      .use(assistedSelectionPlugin);

    if (state.get('subAccount_id')) {
      gridFactory = gridFactory.use(paginationPlugin);
    } else {
      gridFactory = gridFactory.use(treePlugin)
        .use(ignoreServerPaginationPlugin);
    }
    this.grid = gridFactory.create(this.gridOptions);

    this.grid.gridView.on('didSelect', (selected) => {
      this.onSelected(selected);
    });

    this.grid.gridView.on('didUpdate', this.onGridUpdated, this);

    $('.hotel-groups-list-grid-container').append(this.grid.gridView.render().el);
  }

  refreshGrid() {
    this.grid.gridView.refresh(true);
  }

  fetchAssociations() {
    const odataOptions = {
      cacheResponse: false,
      url: gridOptionUtils.getUrlByName('associations'),
    };

    return jsdata.HotelAssociation.findAll({}, odataOptions);
  }

  disableDownload(disabled) {
    const hasDisabled = this.$('.associations-download__container .btn').hasClass('disabled');

    if (!hasDisabled && disabled) {
      this.$('.associations-download__container .btn').addClass('disabled');
    } else if (hasDisabled && !disabled) {
      this.$('.associations-download__container .btn').removeClass('disabled');
    }
  }

  onDownloadAssociations() {
    this.fetchAssociations()
      .then((response) => {
        if (response.totalCount === 0) {
          this.disableDownload(true);
          return;
        }
        const localizedHeaders = [
          i18n.getString('HotelCenter_Overview_Grid_Hotel_Advertiser_Hotel_ID_Label'),
          i18n.getString('HotelCenter_Group_Hotel_Name_Label'),
          i18n.getString('HotelCenter_Group_Name_Label'),
          i18n.getString('HotelCenter_Overview_Grid_Shared_SubAccount_Name_Label'),
        ];
        const odataColumnNames = ['PartnerHotelId', 'HotelName', 'HotelGroupName', 'SubAccountName'];
        const downloadView = new DownloadViewV1({
          localizedHeaders,
          odataColumnNames,
          filterUrlParam: '',
          segmentationTypes: [],
          entity: 'HotelAssociation',
          gridId: 'HotelGroupAssociationsDownloadGrid',
          totalRecordCount: response.totalCount,
          processReportRule: ReportRule => _.defaults({
            Selection: _.defaults({
              CampaignId: state.get('subAccount_id') ? Number(state.get('subAccount_id')) : undefined,
            }, ReportRule.Selection),
          }, ReportRule),
        });

        downloadView.download();
        downloadView.remove();
      });
  }

  onShowCreateDialog() {
    const entitySet = edm.currentAccount
      .SubAccounts.$withKey(this.subAccountId.toString())
      .HotelGroups;
    const hotelGroupModel = new HotelGroupModel({ entitySet });

    const hotelNameInput = new EditControl({
      entity: hotelGroupModel,
      field: 'Name',
      description: i18n.getString('HotelCenter_Group_Create_Description'),
    });

    hotelGroupModel.errors.on('change:Name', (vm, error) => {
      if (_.isEmpty(error)) {
        $('.hotel-center-group-dialog-save').removeClass('disabled');
      } else {
        $('.hotel-center-group-dialog-save').addClass('disabled');
      }
    });

    const dialog = new Dialog({ className: 'hotel-center-group-dialog' }).setContent(hotelNameInput).setState({
      title: i18n.getString('HotelCenter_Group_Create_Title', this.getState().groupName),
      buttons: [{
        text: i18n.getString('HotelCenter_Shared_Create'),
        name: 'confirm',
        class: 'hotel-center-group-dialog-save',
      }, {
        text: i18n.getString('HotelCenter_Shared_Cancel'),
        name: 'cancel',
        class: 'dialog-cancel',
      }],
    });

    document.body.appendChild(dialog.el);
    dialog.show();
    $('.hotel-center-group-dialog-save').addClass('disabled');

    dialog.on('clickedButton', (name) => {
      if (name === 'confirm') {
        this._createHotelGroupAsync(hotelGroupModel).then((isSuccess) => {
          if (isSuccess) {
            dialog.remove();
          } else {
            $('.hotel-center-group-dialog-save').addClass('disabled');
          }
        });
      } else {
        dialog.remove();
      }
    });
  }

  _createHotelGroupAsync(hotelGroupModel) {
    return hotelGroupModel
      .save()
      .then((savedResult) => {
        const isSubAccountLevel = state.get('subAccount_id') === '';

        if (!isSubAccountLevel) {
          const { pageSize } = this.grid.pagination;
          const totalCount = this.grid.gridView.totalCountRows;
          const pageNumber = Math.floor(totalCount / pageSize);

          this.grid.pagination.pageNumber = pageNumber;
        }
        if (savedResult) {
          this.grid.gridView.selectRow([savedResult.value]);
        }
        this.grid.gridView.refresh(true);

        return true;
      })
      .catch((e) => {
        let errorCode = 'CreateFailed';

        if (e && e.body && e.body.value && e.body.value.length > 0) {
          errorCode = e.body.value[0].Code;
        }

        // Error is expecting an array to be set.
        hotelGroupModel.errors.set('Name', [this.errorCodeToStringMap[errorCode]]);

        return false;
      });
  }

  onGridUpdated() {
    const isSubAccountLevel = state.get('subAccount_id') === '';

    if (this.grid.gridView.selectedItems().length < 1 && this.grid.gridView.items.length > 0) {
      this.grid.gridView.selectRow(this.grid.gridView.items.slice(0, 1)[0].Id);
    } else if (isSubAccountLevel
        && this.grid.gridView.items.length === 0 && !this.grid.pagination) {
      // handle no subaccount
      this.trigger('noSubAccount');
    }
    // handle pagination change
    if (!isSubAccountLevel) {
      if (this.grid.gridView.items.length === 0 && _.result(this.grid.pagination, 'pageNumber') > 0) {
        this.grid.pagination.pageNumber = this.grid.pagination.pageNumber - 1;
      }
    }
  }

  onSelected(selected) {
    if (selected.length > 1) {
      // deselected all but the last one.
      _.each(selected.slice(0, selected.length - 1), (key) => {
        this.grid.gridView.deselectRow(key);
      });

      // select only the last one.
      this.grid.gridView.selectRow(selected[selected.length - 1]);
    }

    if (this.grid.gridView.selectedItems().length !== 0) {
      const selectedItem = this.grid.gridView.selectedItems()[0];

      if (this.selected !== selectedItem) {
        this.selected = selectedItem;
        if (this.selected.childrenKeys && this.selected.childrenKeys.length > 0) {
          // subaccount is selected
          const ungroupedGroup = this.grid.gridView
            .itemWithKey(_.first(this.selected.childrenKeys));

          this.subAccountId = this.selected.Id;
          this.trigger('selectionChanged', {
            selectedGroup: ungroupedGroup,
            selectedSubAccount: this.selected,
            isSubAccountSelected: true,
            ungroupedGroupId: '',
          });
        } else if (_.has(this.selected, 'childrenKeys')) {
          const parentNode = this.grid.gridView.itemWithKey(selectedItem.ParentId);
          const ungroupedGroup = this.grid.gridView.itemWithKey(_.first(parentNode.childrenKeys));

          this.subAccountId = parentNode.Id;
          this.trigger('selectionChanged', {
            selectedGroup: selectedItem,
            ungroupedGroupId: ungroupedGroup.Id,
            selectedSubAccount: parentNode,
          });
        } else {
          const ungroupedGroup = this.grid.gridView.items.slice(0, 1)[0];

          this.subAccountId = state.get('subAccount_id');
          this.trigger('selectionChanged', {
            selectedGroup: selectedItem,
            ungroupedGroupId: ungroupedGroup ? ungroupedGroup.Id : null,
            selectedSubAccount: { Id: this.subAccountId },
          });
        }
      }
    }
  }
}
